import {Loader2Icon} from "lucide-react";
import {Navigate, Outlet, useLocation} from "react-router-dom";

import {useAuth} from "@/lib/auth";

interface AuthGateProps {
  /** When set, requires the principal's scope to match. */
  role?: "user" | "account";
}

/**
 * Route wrapper that gates child routes by authentication and (optionally)
 * principal scope. Shows a centered spinner while the auth context is
 * restoring tokens / hydrating /auth/me on boot.
 *
 * - If unauthenticated: redirects to /login (or /admin/login when role=account).
 * - If wrong role: redirects to a sensible home for the actual scope.
 */
export const AuthGate = ({role}: AuthGateProps) => {
  const {token, scope, loading} = useAuth();
  const loc = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!token) {
    const target = role === "account" ? "/admin/login" : "/login";
    return (
      <Navigate
        to={`${target}?to=${encodeURIComponent(loc.pathname + loc.search)}`}
        replace
      />
    );
  }

  if (role && scope !== role) {
    // Wrong principal type — bounce to the right home.
    if (role === "account") {
      // Customer trying to access /admin/* → send them to customer home.
      return <Navigate to="/servers" replace />;
    }
    // Admin trying to access customer-only — send to admin home.
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
};
